import { Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { HttpCacheOperacion } from 'src/pages/sistema/api/httpCacheOperacion'
import { SectorComercialFields } from 'src/types'
import { AsyncSelect, ControlledDatePicker, ControlledTextField } from "../../../../../components/forms"
import { useLoadOptions } from "../../../../../hooks/useLoadOptions"
import CardActions from '../../../components/CardActions'
import { SectionBox } from '../../../components/FieldView'
import { useLCLSSectorComercialTipos, useLCLSUnidadNegocioTipos } from "../hooks/useLCLSCache"
import { OperacionLCLSRelacion } from '../types/dto'
import { OperacionLCLSCuentaFields, OperacionLCLSFields, OperacionLCLSRelacionFields } from "../types/fields"
import { FormOperacionLCLS, FormOperacionLCLSCuenta } from "../types/types"

interface SeccionNuevoLCLSDatosOperacionProps {
    formMethods: UseFormReturn<FormOperacionLCLS>
    codSistemaPrincipal: number,
    codSistema: number,
}

export const SeccionNuevoLCLSDatosOperacion = ({
    formMethods,
    codSistemaPrincipal,
    codSistema
}: SeccionNuevoLCLSDatosOperacionProps) => {
    const [relaciones, setRelaciones] = useState<OperacionLCLSRelacion[]>([]);
    const { control, setValue } = formMethods
    const unidadNegocioTipos = useLCLSUnidadNegocioTipos(codSistemaPrincipal, codSistema);
    const loadUnidadNegocio = useLoadOptions({
        data: unidadNegocioTipos.unidadNegocioTipos,
        loading: unidadNegocioTipos.isLoading,
        isError: unidadNegocioTipos.isError,
        error: unidadNegocioTipos.error
    });

    const watchedUnidadNegocio = useWatch({ control: control, name: OperacionLCLSFields.CodUnidadNegocio });
    const watchedSectorComercial = useWatch({ control: control, name: OperacionLCLSFields.CodSectorComercial });

    const sectorComercialTipos = useLCLSSectorComercialTipos(watchedUnidadNegocio, codSistema);
    const loadSectorComercial = useLoadOptions({
        data: sectorComercialTipos.sectorComercialTipos,
        loading: sectorComercialTipos.isLoading,
        isError: sectorComercialTipos.isError,
        error: sectorComercialTipos.error
    });

    useEffect(() => {
        // Cuando cambia la unidad de negocio, reseteamos el sector comercial y las cuentas
        setValue(OperacionLCLSFields.CodSectorComercial, null as any);
        setValue(OperacionLCLSFields.Cuentas, []);
    }, [watchedUnidadNegocio, setValue]);

    useEffect(() => {
        if (!watchedSectorComercial) {
            setValue(OperacionLCLSFields.Cuentas, []);

            return;
        }

        const generarCuentas = async () => {
            const relaciones = await HttpCacheOperacion.getLCLSRelacionTipos(watchedSectorComercial);

            const nuevasCuentas: FormOperacionLCLSCuenta[] = relaciones.map(relacion => ({
                [OperacionLCLSCuentaFields.CodClienteTipo]: relacion[OperacionLCLSRelacionFields.CodClienteTipo],
                [OperacionLCLSCuentaFields.DescClienteTipo]: relacion[OperacionLCLSRelacionFields.DescClienteTipo],
                [OperacionLCLSCuentaFields.CodClienteSubTipo]: relacion[OperacionLCLSRelacionFields.CodClienteSubTipo],
                [OperacionLCLSCuentaFields.DescClienteSubTipo]: relacion[OperacionLCLSRelacionFields.DescClienteSubTipo],

                [OperacionLCLSCuentaFields.NroCuenta]: '',
                [OperacionLCLSCuentaFields.RazonSocial]: '',
                [OperacionLCLSCuentaFields.Moneda]: 'Dólares',

                // el null as any es porque el schema valida el valor, y aca queremos crearla vacia. asi no tenemos que modificar el schema para que permita nulls
                [OperacionLCLSCuentaFields.LimiteConcertacion]: "" as any,
                [OperacionLCLSCuentaFields.LimiteLiquidacionDVP]: "" as any,
                [OperacionLCLSCuentaFields.LimiteLiquidacionFOP]: "" as any,
            }));

            setValue(OperacionLCLSFields.Cuentas, nuevasCuentas);
        };

        generarCuentas();
    }, [watchedSectorComercial, setValue]);

    return (
        <CardActions title='Datos Operación' icon='mdi:briefcase-outline' colapsable>
            <SectionBox>
                <Grid item xs={12} md={4}>
                    <AsyncSelect
                        name={OperacionLCLSFields.CodUnidadNegocio}
                        loadOptions={loadUnidadNegocio}
                        control={control}
                        valueField={SectorComercialFields.UnidadNegocioCodigo}
                        label='Unidad de negocio'
                        selectNotNulleable
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <AsyncSelect
                        name={OperacionLCLSFields.CodSectorComercial}
                        loadOptions={loadSectorComercial}
                        valueField={SectorComercialFields.SectorComercialCodigo}
                        control={control}
                        label='Equipo Comercial'
                        selectNotNulleable
                        disabled={!watchedUnidadNegocio}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ControlledDatePicker
                        name={OperacionLCLSFields.FechaSolicitud}
                        control={control}
                        label='Fecha Solicitud'
                        disabled
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ControlledDatePicker
                        name={OperacionLCLSFields.FechaVencimiento}
                        control={control}
                        label='Fecha Vencimiento'
                    />
                </Grid>
                <Grid item xs={12}>
                    <ControlledTextField
                        control={control}
                        name={OperacionLCLSFields.Observaciones}
                        label='Observaciones'
                        multiline={true}
                        minRows={3}
                        fullWidth
                    />
                </Grid>
            </SectionBox>
        </CardActions>
    )
}
